import { observable, makeAutoObservable, reaction, runInAction } from "mobx";
import { createContext } from "react";
import {
  FAN_RECENT_GAMES_PAGE_SIZE,
  getRecentGamesRequest,
} from "../axios/routes/game";
import { IntervalGamePopulated } from "../shared/interfaces";
import { ONGOING_GAME_START_OFFSET_MS } from "../utils/constants";
import { showErrorNotification } from "../utils/notification";
import { FAN_DEDICATED_SUBDOMAIN_TEAM_NAME, isFanFirstPartyHostPrefix } from "../utils/fanFlowNavigation";
import {
  getFanHostnamePrefix,
  shouldDeferRecentGamesFetch,
} from "../utils/fanHostnameGamesFilter";
import { StorageServiceInstance } from "./storage";

/** ms to wait before recent-games fetch on moment-heavy routes (after moment images start). */
const DEFERRED_RECENT_GAMES_MS = 5000;

export type GameType = "all"|"live"|"finished"|"future";

class GameService {
  private games: IntervalGamePopulated[] = [];

  @observable pastGames: IntervalGamePopulated[] = [];

  @observable futureGames: IntervalGamePopulated[] = [];

  @observable liveGames: IntervalGamePopulated[] = [];

  @observable allGames: IntervalGamePopulated[] = [];

  @observable dateToFetchRecentGames: Date = null;

  /**
   * True while a games-list fetch is in flight. The fan Games page uses this
   * to render a spinner instead of an empty list. Initialized true because the
   * constructor immediately kicks off `fetchRecentGames`.
   */
  @observable gamesLoading = true;

  /** True while a Load-more page request is in flight. */
  @observable gamesLoadingMore = false;

  /** Server indicated another page may exist (last page returned a full page size). */
  @observable hasMoreRecentGames = false;

  private recentGamesOffset = 0;

  private recentGamesTeamId: string | undefined;

  private recentGamesHostname: string | undefined;

  constructor() {
    makeAutoObservable(this);

    if (shouldDeferRecentGamesFetch()) {
      window.setTimeout(() => {
        console.log("> Fetching recent games (deferred for moment route)");
        this.fetchRecentGames(new Date());
      }, DEFERRED_RECENT_GAMES_MS);
    } else {
      console.log("> Fetching recent games");
      this.fetchRecentGames(new Date());
    }

    reaction(() => this.games, () => this.sortResentGames());
  }

  /** Prefer a single home team id; otherwise scope by hostname (multi-club hosts). */
  private resolveRecentGamesScope = (): { teamId?: string; hostname?: string } => {
    const hubTeamId = StorageServiceInstance.hubSelectedHomeTeamId;
    if (hubTeamId) {
      return { teamId: hubTeamId };
    }

    const welcomeId = StorageServiceInstance.welcomeTeamContext?._id;
    if (
      welcomeId
      && !StorageServiceInstance.fanFlowRequiresTeamPicker
    ) {
      return { teamId: String(welcomeId) };
    }

    // Hub / app hosts (e.g. mom-dev-client) are not club hostnames — do not filter
    // recent games to Team.hostname === "mom-dev-client" (always empty).
    if (StorageServiceInstance.fanFlowRequiresTeamPicker) {
      return {};
    }

    const hostnamePrefix = getFanHostnamePrefix();
    if (hostnamePrefix && !isFanFirstPartyHostPrefix(hostnamePrefix)) {
      return { hostname: hostnamePrefix };
    }
    return {};
  };

  private applyClientTeamSafetyFilter = (games: IntervalGamePopulated[]): IntervalGamePopulated[] => {
    const hostnamePrefix = getFanHostnamePrefix();
    const isHubFlow = StorageServiceInstance.fanFlowRequiresTeamPicker;

    if (!isHubFlow && hostnamePrefix !== "") {
      const gamesFilteredByHostname = games.filter((game) => (
        (game.team1?.hostname || "").toLowerCase() === hostnamePrefix
      ));

      if (gamesFilteredByHostname.length > 0) {
        return gamesFilteredByHostname;
      }
      const normalizedHostname = (
        FAN_DEDICATED_SUBDOMAIN_TEAM_NAME[hostnamePrefix] || hostnamePrefix
      ).toLowerCase();
      const gamesFilteredByTeamName = games.filter((game) => (
        (game.team1?.name?.toLowerCase() ?? "") === normalizedHostname
      ));
      if (gamesFilteredByTeamName.length > 0) {
        return gamesFilteredByTeamName;
      }
      return games;
    }

    if (isHubFlow && StorageServiceInstance.hubSelectedHomeTeamName) {
      const hn = StorageServiceInstance.hubSelectedHomeTeamName;
      return games.filter((game) => (
        (game.team1?.name?.toLowerCase() ?? "") === hn
      ));
    }

    return games;
  };

  fetchRecentGames = async (date: Date) => {
    if (this.dateToFetchRecentGames && this.dateToFetchRecentGames.getTime() === date.getTime()) {
      console.log("> Recent games are already fetched");
      return;
    }

    this.dateToFetchRecentGames = date;
    this.gamesLoading = true;
    this.recentGamesOffset = 0;
    const scope = this.resolveRecentGamesScope();
    this.recentGamesTeamId = scope.teamId;
    this.recentGamesHostname = scope.hostname;

    try {
      const page = await getRecentGamesRequest({
        start: date.getTime(),
        teamId: scope.teamId,
        hostname: scope.teamId ? undefined : scope.hostname,
        limit: FAN_RECENT_GAMES_PAGE_SIZE,
        offset: 0,
      });

      runInAction(() => {
        this.games = this.applyClientTeamSafetyFilter(page);
        this.recentGamesOffset = page.length;
        this.hasMoreRecentGames = page.length >= FAN_RECENT_GAMES_PAGE_SIZE;
        this.sortResentGames();
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err ?? "Unknown error");
      showErrorNotification(msg);
    } finally {
      runInAction(() => {
        this.gamesLoading = false;
      });
    }
  };

  /** Append the next page of team-scoped recent games. */
  loadMoreRecentGames = async (): Promise<void> => {
    if (this.gamesLoadingMore || this.gamesLoading || !this.hasMoreRecentGames) {
      return;
    }
    const start = this.dateToFetchRecentGames?.getTime() ?? Date.now();
    this.gamesLoadingMore = true;
    try {
      const page = await getRecentGamesRequest({
        start,
        teamId: this.recentGamesTeamId,
        hostname: this.recentGamesTeamId ? undefined : this.recentGamesHostname,
        limit: FAN_RECENT_GAMES_PAGE_SIZE,
        offset: this.recentGamesOffset,
      });
      const filtered = this.applyClientTeamSafetyFilter(page);
      runInAction(() => {
        const seen = new Set(this.games.map((g) => String(g._id)));
        const appended = filtered.filter((g) => !seen.has(String(g._id)));
        this.games = [...this.games, ...appended];
        this.recentGamesOffset += page.length;
        this.hasMoreRecentGames = page.length >= FAN_RECENT_GAMES_PAGE_SIZE;
        this.sortResentGames();
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err ?? "Unknown error");
      showErrorNotification(msg);
    } finally {
      runInAction(() => {
        this.gamesLoadingMore = false;
      });
    }
  };

  sortResentGames = () => {
    this.pastGames = [];
    this.futureGames = [];
    this.liveGames = [];

    const currentTime = new Date().getTime();

    this.games.forEach((game) => {
      const ongoingThreshold = game.startDate - ONGOING_GAME_START_OFFSET_MS;
      if (game.finishDate && game.finishDate < currentTime) {
        this.pastGames.push(game);
      } else if (ongoingThreshold <= currentTime) {
        this.liveGames.push(game);
      } else {
        this.futureGames.push(game);
      }
    });

    this.allGames = [...this.liveGames, ...this.futureGames, ...this.pastGames];
  }

  /** After hub team selection, refetch so filtering uses the new club. */
  refetchRecentGamesForHubPick = async (): Promise<void> => {
    runInAction(() => {
      this.dateToFetchRecentGames = null;
      this.hasMoreRecentGames = false;
      this.recentGamesOffset = 0;
    });
    await this.fetchRecentGames(new Date());
  };
}

export const GameServiceInstance = new GameService();

export const GameServiceContext = createContext(GameServiceInstance);
